import { useMemo } from 'react';
import type { Table } from '@tanstack/react-table';
import { Check, Columns3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { DEFAULT_ADVANCED_TABLE_LABELS, type AdvancedTableLabels } from './advancedTableLabels';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AdvancedColumnVisibilityProps<TData> {
  table: Table<TData>;
  labels?: Partial<AdvancedTableLabels>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdvancedColumnVisibility<TData>({
  table,
  labels,
}: AdvancedColumnVisibilityProps<TData>) {
  const l = { ...DEFAULT_ADVANCED_TABLE_LABELS, ...labels };

  const columns = useMemo(
    () =>
      table
        .getAllColumns()
        .filter(
          (column) =>
            typeof column.accessorFn !== 'undefined' && column.getCanHide(),
        ),
    [table],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" aria-label={l.columns}>
          <Columns3 className="h-3.5 w-3.5" />
          {l.columns}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput placeholder={l.columnsSearch} />
          <CommandList>
            <CommandEmpty>{l.columnsEmpty}</CommandEmpty>
            <CommandGroup>
              {columns.map((column) => (
                <CommandItem
                  key={column.id}
                  onSelect={() =>
                    column.toggleVisibility(!column.getIsVisible())
                  }
                >
                  <span className="truncate">
                    {column.columnDef.meta?.label ?? column.id}
                  </span>
                  <Check
                    className={cn(
                      'ml-auto h-4 w-4 shrink-0',
                      column.getIsVisible() ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
